import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { ds } from '@/config/colors';
import { media } from '@/utils';

import { HelpAiChat } from './HelpAiChat';
import { useHelpAiChat } from './HelpAiChatContext';

/**
 * Asystent AI panelu „Stefan" dokowany przy prawej krawędzi ekranu (wzór
 * ElevenLabs). Otwiera go guzik „Zapytaj" w TopBarze (globalny stan w
 * `HelpAiChatContext`). Na każdej szerokości to szuflada nad treścią,
 * z białym rozmytym tłem (klik w tło zamyka).
 *
 * Reużywa istniejący `HelpAiChat` — tylko opakowanie. Backend bez zmian.
 */

export const DOCK_WIDTH = 400;

/* Tło pod szufladą tylko na tablecie i telefonie (biały blur, klik zamyka).
   Na desktopie szuflada wyjeżdża z krawędzi containera jak sidebar, bez tła. */
const Overlay = styled.div<{ $open: boolean }>`
  display: none;

  ${media.tabletDown} {
    display: block;
    position: fixed;
    inset: 0;
    z-index: 1000;
    background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(15, 20, 25, 0.5)' : 'rgba(255, 255, 255, 0.5)')};
    -webkit-backdrop-filter: blur(3px);
    backdrop-filter: blur(3px);
    opacity: ${({ $open }) => ($open ? 1 : 0)};
    visibility: ${({ $open }) => ($open ? 'visible' : 'hidden')};
    transition: opacity 0.28s ease, visibility 0.28s ease;
  }
`;

/* Okno, z którego wyjeżdża szuflada. Na desktopie przyklejone do prawej krawędzi
   containera i pod paskiem górnym, więc panel wysuwa się zza granicy treści,
   a nie zza krawędzi ekranu. `overflow: hidden` chowa zamknięty panel. */
const DockClip = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 1001;
  width: ${DOCK_WIDTH}px;
  max-width: 100vw;
  overflow: hidden;
  pointer-events: none;

  /* 65 px: zaczyna się pod linią paska górnego (cień 1 px pod 64 px), nie zasłania jej. */
  ${media.desktop} {
    top: 65px;
    right: var(--hs-gutter, 0px);
  }

  @media (max-width: 480px) {
    width: 100vw;
  }
`;

const Dock = styled.aside<{ $open: boolean }>`
  position: absolute;
  inset: 0;
  transform: translateX(${({ $open }) => ($open ? '0' : '100%')});
  transition: transform 0.32s cubic-bezier(0.32, 0.72, 0, 1);
  box-shadow: none;
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
  border-left: 1px solid ${({ theme }) => (theme.mode === 'dark' ? '#1f2937' : ds.borderDefault)};
`;

/* Kolumna wewnątrz doku: opcjonalny banner „agent chce się nauczyć" na górze
   (renderowany wewnątrz HelpAiChat) + czat wypełniający resztę wysokości. */
const Panel = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: ${({ theme }) => (theme.mode === 'dark' ? '#0f1419' : '#ffffff')};

  /* HelpAiChat ma własną Card (height: 460px, align-self: start, border-radius:
     12px) — w trybie doku wypełnia całą szerokość i resztę wysokości oraz ma
     ścięte zaokrąglenia przy krawędzi. */
  & > div:last-child {
    flex: 1;
    min-height: 0;
    height: auto;
    width: 100%;
    align-self: stretch;
    border-radius: 0;
    border: none;
  }
`;

export const DockedHelpAiChat: FC = () => {
  const { t } = useTranslation();
  const { isOpen, hasOpened, close } = useHelpAiChat();

  // Blokada przewijania strony gdy panel otwarty — na desktopie treść i tak ma
  // `overflow: hidden` (Main), ale na tablecie/telefonie przewija się `body`,
  // więc chowamy jego scroll i zwracamy poprzednią wartość przy zamknięciu.
  useEffect(() => {
    if (!isOpen) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen]);

  return (
    <>
      <Overlay $open={isOpen} onClick={close} aria-hidden="true" />

      <DockClip>
        <Dock
          $open={isOpen}
          role="dialog"
          aria-modal="false"
          aria-hidden={!isOpen}
          aria-label={t('helpAiChat.title', 'Asystent AI')}
        >
          {hasOpened && (
            <Panel>
              {/* Banner „agent chce się czegoś nauczyć" renderuje się wewnątrz
                  HelpAiChat, pod headerem Stefana. Guzik X zamykający panel jest
                  teraz w headerze Stefana (onClose). */}
              <HelpAiChat onClose={close} />
            </Panel>
          )}
        </Dock>
      </DockClip>
    </>
  );
};
